// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@pace/App/Components/Common/XngZone";
import { checkoutPages } from "@pace/XDL/constants";
import { mergeWithCss, xClassNames } from "@pace/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

/**
 * XNG-Changes
 * Make number of link lists dynamic
 */
const enum fields {
    linkListsCount = "linkListsCount",
}

interface OwnProps extends WidgetProps {
    // XNG-Changes
    fields: {
        [fields.linkListsCount]: number;
    };
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    /**
     * XNG-Changes
     */
    container?: GridContainerProps;
    mainWrapper?: InjectableCss;
    actionWrapper?: InjectableCss;
    linkListsWrappers?: InjectableCss;
    secondaryWrapper?: InjectableCss;
    imagesWrapper?: InjectableCss;
    contentBlockWrapper?: InjectableCss;
}

export const footerContainerStyles: FooterContainerStyles = {
    logoGridItem: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            img {
                height: 78px;
            }
        `,
    },
    linkList1GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList2GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList3GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList4GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    subscribeGridItem: {
        width: [12, 12, 8, 4, 4],
    },
    socialLinksGridItem: {
        width: 12,
    },
    siteMapLinksGridItem: {
        width: 12,
    },
    richContentGridItem: {
        width: 12,
    },
    logoWrapper: {
        css: css`
            width: 110px;
        `,
    },
};

const FooterContainer: FC<OwnProps> = ({ id, fields, extendedStyles }) => {
    const [styles] = React.useState(() => mergeWithCss(footerContainerStyles, extendedStyles));

    // XNG-Changes
    const { isCheckoutPage } = useAppSelector(state => ({
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
    }));
    /**
     * XNG-Changes: make linkList dynamic
     */
    const { isEditing } = useShellContext();

    const linkListsCount: JSX.Element[] = [];
    for (let row = 0; row < fields.linkListsCount; row = row + 1) {
        linkListsCount.push(
            <XngZone contentId={id} zoneName={`LinkList-${row + 1}`} key={row + 1} normalWidth={false} />,
        );
    }

    // XNG-Changes: Update Structure
    return (
        <StyledWrapper {...styles.container}>
            {!isCheckoutPage && (
                <StyledWrapper {...styles.widgetGridContainer}>
                    <StyledWrapper {...styles.mainWrapper}>
                        <StyledWrapper {...styles.logoWrapper}>
                            <XngZone zoneName="Logo" contentId={id} />
                            <XngZone zoneName="SocialLinks" contentId={id} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.contentBlockWrapper}>
                            <XngZone zoneName="Messages" contentId={id} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.imagesWrapper}>
                            <XngZone zoneName="Images" contentId={id} />
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper
                        {...styles.actionWrapper}
                        className={xClassNames.append(isEditing && "shell-editing")}
                    >
                        <XngZone zoneName="Subscribe" contentId={id} />
                        {linkListsCount.length > 0 && (
                            <StyledWrapper {...styles.linkListsWrappers}>
                                {linkListsCount.map(item => item)}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.secondaryWrapper} {...{ isCheckoutPage }}>
                {/** XNG-Changes: PAC-1021 */}
                <Typography size={12} lineHeight="20px" color="common.ui1" style={{ whiteSpace: "nowrap" }}>
                    {siteMessage("Custom_Footer_Copyright")
                        .toString()
                        .replace("{0}", new Date().getFullYear().toString())}
                </Typography>
                <XngZone zoneName="SiteMapLinks" contentId={id} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
        fieldDefinitions: [
            {
                name: fields.linkListsCount,
                displayName: "Row Count",
                editorTemplate: "IntegerField",
                defaultValue: 4,
                max: 1,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default footerContainer;
